import {Icon, ThemeIconKey, Show} from 'platform/foundation';

import {MouseEvent} from 'react';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useActionLocksForRow} from '../../../../store/selectors/useActionLocksForRow';
import type {ActionDefinitionObject} from '../../../../types/ActionDefinition';
import type {RowData} from '../../../../types/RowData';
import {partitionRowActions} from '../../../../utils/partitionRowActions';
import {MobileSheet} from '../../../MobileSheet/MobileSheet';

export type MobileRowActionSheetProps = {
  row: RowData;
  title: string;
  actionsDefinition: ActionDefinitionObject;
  onActionClick: (actionKey: string, row: RowData, event: MouseEvent) => void;
  onClose: () => void;
  /** Sheet-wide disable — mirrors RowActions' `isDisabled` (GridBody's `disableAllActions`). */
  isDisabled?: boolean;
} & TestIdProps;

/**
 * Mobile bottom sheet listing every action for a single row — replaces the per-row
 * inline icon buttons + overflow dropdown on small screens, where there isn't enough
 * horizontal room for them. Built on MobileSheet (portals to document.body).
 *
 * Order mirrors desktop precedence: the promoted rowClick action first (styled as the
 * primary/highlighted item), then icon (primary) actions, then overflow (secondary) actions.
 */
export function MobileRowActionSheet(props: MobileRowActionSheetProps) {
  const {iconActions, overflowActions, rowClickAction} = partitionRowActions(
    props.row.actions,
    props.actionsDefinition
  );
  // Live lock state for this row — the sheet stays mounted while an action is in flight
  // (e.g. a confirm-then-dispatch promise), so this must re-render as locks change, not
  // snapshot them once through props.
  const lockedActionKeys = useActionLocksForRow(props.row.id);

  // Plain derivation — not memoized, matches RowActionsOverflow's precedent (React Compiler
  // auto-memoizes; parent only mounts this component while the sheet is open).
  const orderedActions = [
    ...(rowClickAction ? [{...rowClickAction, isPrimary: true}] : []),
    ...iconActions.map((action) => ({...action, isPrimary: false})),
    ...overflowActions.map((action) => ({...action, isPrimary: false})),
  ];

  const handleItemClick = (actionKey: string, isItemDisabled: boolean) => (event: MouseEvent) => {
    // Belt-and-braces guard alongside the native `disabled` attribute: a locked/in-flight
    // action must neither dispatch nor close the sheet, matching desktop's disabled treatment
    // (RowActions.tsx disables the button outright via lockedActionKeys).
    if (isItemDisabled) {
      return;
    }
    props.onClose();
    props.onActionClick(actionKey, props.row, event);
  };

  return (
    <MobileSheet
      isOpen
      onClose={props.onClose}
      title={props.title}
      data-testid={suffixTestId('rowActionSheet', props)}
    >
      <div className="flex flex-col px-2 pb-4">
        {orderedActions.map((action) => {
          const iconValue = action.definition.icon.value as ThemeIconKey | undefined;
          const isItemDisabled =
            props.isDisabled === true || lockedActionKeys.has(action.actionKey);

          return (
            <button
              key={action.actionKey}
              type="button"
              disabled={isItemDisabled}
              className={`flex items-center gap-3 w-full text-left px-3 py-3 text-[15px] rounded-lg ${
                action.isPrimary ? 'text-[#0065FF] font-semibold' : 'text-[#091E42]'
              } ${isItemDisabled ? 'opacity-40 cursor-not-allowed' : 'active:bg-slate-100'}`}
              onClick={handleItemClick(action.actionKey, isItemDisabled)}
              data-testid={suffixTestId(`rowActionSheetItem-${action.actionKey}`, props)}
            >
              <Show when={iconValue}>
                <Icon value={iconValue} size={5} />
              </Show>
              <span className="flex-1">{action.definition.title}</span>
            </button>
          );
        })}
      </div>
    </MobileSheet>
  );
}
